import React, { useState } from "react";
import {
  Funnel,
  File,
  CirclePlus,
  Download,
  UnfoldVertical,
  FoldVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Menubar, MenubarMenu, MenubarTrigger } from "@/components/ui/menubar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Table } from "@tanstack/react-table";
import { Product, Status } from "@/types/product";
import { useHeader } from "./hooks";
import ImportModal from "./modal";

interface Props {
  table: Table<Product>;
}

const Header: React.FC<Props> = ({ table }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentStatusFilter = table.getColumn("status")?.getFilterValue();

  const { onAddProductClick, onStatusFilterClick, onImportProductClick } =
    useHeader(table, setIsModalOpen);

  return (
    <>
      <div className="flex flex-col gap-4 pb-4">
        <div>
          <Menubar className="w-fit bg-slate-100 rounded-md border-0 text-slate-500 p-1 flex justify-between">
            <MenubarMenu>
              {["All", "published", "draft", "archived"].map((status) => {
                const isActive =
                  (status === "All" &&
                    (!currentStatusFilter || currentStatusFilter === "")) ||
                  currentStatusFilter === status;

                const handleClick = () =>
                  onStatusFilterClick(
                    status === "All" ? undefined : (status as Status)
                  );

                return (
                  <MenubarTrigger
                    key={status}
                    onClick={() => handleClick()}
                    className={`transition-all duration-300 ease-in-out ${
                      isActive ? "text-secondary bg-white" : "hover:bg-gray-200"
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenubarTrigger>
                );
              })}
            </MenubarMenu>
          </Menubar>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Input
              placeholder="Search..."
              value={
                (table.getColumn("title")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("title")?.setFilterValue(event.target.value)
              }
              className="max-w-xs"
            />
            <Button variant="outline" className="ml-auto" disabled>
              <CirclePlus /> Status
            </Button>
            <Button variant="outline" className="ml-auto" disabled>
              <CirclePlus /> Category
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => table.toggleAllRowsExpanded()}
            >
              {table.getIsAllRowsExpanded() ? (
                <>
                  <FoldVertical /> Collapse all
                </>
              ) : (
                <>
                  <UnfoldVertical /> Expand all
                </>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  <Funnel /> Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="outline" className="ml-auto" disabled>
              <File /> Export
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="ml-auto"
                    disabled={true}
                    onClick={onAddProductClick}
                  >
                    <CirclePlus /> Add Product
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>If you want to change this please contact us</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              disabled
              variant="outline"
              className="ml-auto"
              onClick={onImportProductClick}
            >
              <Download /> Import from Dippi
            </Button>
          </div>
        </div>
      </div>
      {isModalOpen && <ImportModal setIsModalOpen={setIsModalOpen} />}
    </>
  );
};

export default Header;
